import { format } from 'date-fns-tz';

export const getNearestTime = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    // Find the nearest time interval (every 30 minutes)
    const roundedMinute = Math.round(currentMinute / 30) * 30;
    const adjustedHour = roundedMinute === 60 ? (currentHour + 1) % 24 : currentHour;
    const adjustedMinute = roundedMinute === 60 ? 0 : roundedMinute;

    const period = adjustedHour < 12 ? "AM" : "PM";
    const displayHour = adjustedHour === 0 ? 12 : adjustedHour > 12 ? adjustedHour - 12 : adjustedHour;
    const displayMinute = adjustedMinute < 10 ? `0${adjustedMinute}` : adjustedMinute;

    return `${displayHour}:${displayMinute} ${period}`;
};

// Create a list of times at 30-minute intervals
export const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
            const period = hour < 12 ? "AM" : "PM";
            const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
            const displayMinute = minute < 10 ? `0${minute}` : minute;
            times.push(`${displayHour}:${displayMinute} ${period}`);
        }
    }
    return times;
};

export const parseDateTime = (selectedDate: any | Date, startTime: string, endTime: string) => {
    // Parse start and end times
    const startTimeParsed = parseTimeString(startTime);
    const endTimeParsed = parseTimeString(endTime);

    // Create Date objects using the selected date
    const baseDate = new Date(selectedDate);

    // Create start datetime
    const startDateTime = new Date(baseDate);
    startDateTime.setHours(startTimeParsed.hours, startTimeParsed.minutes, 0, 0);

    // Create end datetime
    const endDateTime = new Date(baseDate);
    endDateTime.setHours(endTimeParsed.hours, endTimeParsed.minutes, 0, 0);

    // Handle case where end time is on the next day (e.g., event from 11:30 PM to 1:00 AM)
    if (endDateTime <= startDateTime) {
        endDateTime.setDate(endDateTime.getDate() + 1);
    }

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const startDateTimeString = format(startDateTime, "yyyy-MM-dd'T'HH:mm:ssXXX", { timeZone });
    const endDateTimeString = format(endDateTime, "yyyy-MM-dd'T'HH:mm:ssXXX", { timeZone });
    return {
        startDateTime: startDateTimeString,
        endDateTime: endDateTimeString
    };
}

// Helper function to parse time string (e.g., "9:00 AM" or "21:30")
const parseTimeString = (timeStr: string): { hours: number; minutes: number } => {
    // Handle AM/PM format
    if (timeStr.includes('AM') || timeStr.includes('PM')) {
        const [time, period] = timeStr.split(' ');
        const [hoursStr, minutesStr] = time.split(':');
        let hours = parseInt(hoursStr, 10);
        const minutes = parseInt(minutesStr, 10);

        // Convert to 24-hour format
        if (period === 'PM' && hours !== 12) {
            hours += 12;
        } else if (period === 'AM' && hours === 12) {
            hours = 0;
        }

        return { hours, minutes };
    } else {
        // Handle 24-hour format (e.g., "21:30")
        const [hoursStr, minutesStr] = timeStr.split(':');
        return {
            hours: parseInt(hoursStr, 10),
            minutes: parseInt(minutesStr, 10)
        };
    }
};

export const convertDateTimeToFields = (startDateTime: any, endDateTime: any) => {
    // Helper function to convert 24-hour time to 12-hour format
    const convertTo12Hour = (date: any) => {
        let hours = date?.getHours();
        const minutes = date?.getMinutes();
        const period = hours >= 12 ? 'PM' : 'AM';

        // Convert to 12-hour format
        hours = hours % 12 || 12; // Convert 0 or 12 to 12
        const minutesStr = minutes?.toString().padStart(2, '0'); // Ensure two digits

        return `${hours}:${minutesStr} ${period}`;
    };

    // Extract date from startDateTime (assuming both dates are the same day)
    const selectedDate = new Date(startDateTime);
    selectedDate.setHours(0, 0, 0, 0); // Reset time to 00:00:00 for consistency

    // Convert start and end times to 12-hour format
    const startTime = convertTo12Hour(startDateTime);
    const endTime = convertTo12Hour(endDateTime);

    return { selectedDate, startTime, endTime };
}

export const formatDateLocal = (date: any) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
}

//  Maps API events to the format required by FullCalendar
export const mapApiEventsToFullCalendar = (apiEvents: any[] | unknown): any[] => {
    if (!Array.isArray(apiEvents)) return [];

    return apiEvents
        .filter((event): event is any => !!event)
        .map((event) => ({
            id: event.id,
            title: event.title === "string" ? "Untitled Event" : event.title,
            start: event.startTime,
            end: event.endTime,
            allDay: event.isAllDay,
            className: "border-none rounded-md py-1.5 px-3 w-[100%] text-green-500 !bg-green-100 dark:!bg-green-500/20",
            extendedProps: {
                description: event.description,
                location: event.location,
                timeZone: event.timeZone,
                recurring: event.recurring,
                status: event.status,
                visibility: event.visibility,
                isAllDay: event.isAllDay,
                recurrenceFrequency: event.recurrenceFrequency,
                guest: event.guest,
            },
        }));
};

// Format date for display (e.g., "Sunday, May 4, 2025")
export const formatDate = (date: any) => {
    if (!date) return "";
    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const dayName = days[date.getDay()];
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return `${dayName}, ${month} ${day}, ${year}`;
};

// Function to check for duplicate start times
export const checkDuplicateStartTime = (
    events: any[],
    newStartTime: string | null,
    newEndTime: string | null,
    excludeEventId?: string
): boolean => {
    if (!events || !newStartTime || !newEndTime) return false;

    return events.some((event: any) => {
        if (event.startTime) {
            // Exclude the current event when editing
            if (excludeEventId && event.id === excludeEventId) {
                return false;
            }
            const existingStartTime = new Date(event.startTime).getTime();
            const existingEndTime = new Date(event.endTime).getTime();
            const startTime = new Date(newStartTime).getTime();
            const endTime = new Date(newEndTime).getTime();
            return existingStartTime === startTime || (existingStartTime < startTime && existingEndTime >= startTime) || (endTime >= existingStartTime && endTime <= existingEndTime) || (startTime <= existingStartTime && endTime >= existingEndTime);
        }
        return false;
    });
};